import { equipmentTypeFamiliesState, modelsLinkingDocumentState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { documentTitle, reachChange } from '@rock/manuals';
import type { Model, ModelDocument, ModelDocumentCandidate, SpaceId } from '@rock/types';
import { type FC, type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { listModelNumbers } from './CoverageFields';
import {
    type CoverageDraft,
    type DocumentScope,
    coverageDraftActive,
    coverageSuggestions,
    documentScopeError,
    emptyCoverageDraft,
} from './coverageDraft';
import type { DocumentEdit } from './documentEdits';
import { DocumentFormDialog } from './documentShared';

/**
 * Edit a published document after the fact: what it is (tags, title), what it is for (brands, types)
 * and which models its rule reaches — one form, one save, the same from every page that shows a
 * document. Above Save it says what the edit does to the models already linking the document: the ones
 * linked by rule that it unlinks, and the ones linked directly that it flags.
 */
export const EditDocumentDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    doc: ModelDocument;
    /** The doc's backing candidate — its `coversModels` feed the one-click restore chips. Absent on uploads. */
    candidate?: ModelDocumentCandidate;
    /** Orders the type picker (the page's model's space). */
    spaceId?: SpaceId;
    saving: boolean;
    onSave: (doc: ModelDocument, edit: DocumentEdit) => Promise<boolean>;
}> = ({ open, onOpenChange, doc, ...rest }) => (
    // The form below owns its state; it mounts per open, so every open re-seeds from the stored document.
    <>{open && <EditDocumentForm {...rest} doc={doc} onOpenChange={onOpenChange} />}</>
);

const EditDocumentForm: FC<{
    onOpenChange: (open: boolean) => void;
    doc: ModelDocument;
    candidate?: ModelDocumentCandidate;
    spaceId?: SpaceId;
    saving: boolean;
    onSave: (doc: ModelDocument, edit: DocumentEdit) => Promise<boolean>;
}> = ({ onOpenChange, doc, candidate, spaceId, saving, onSave }) => {
    const [scope, setScope] = useState<DocumentScope>({ brandIds: [...doc.brandIds], typeIds: [...doc.typeIds] });
    const [draft, setDraft] = useState<CoverageDraft>(() =>
        doc.coverage
            ? { prefixes: [...doc.coverage.prefixes], ranges: [...(doc.coverage.ranges ?? [])] }
            : emptyCoverageDraft(),
    );
    return (
        <DocumentFormDialog
            open
            onOpenChange={onOpenChange}
            title={`Edit “${documentTitle(doc)}”`}
            submitLabel="Save"
            initialTags={doc.tags}
            initialTitle={doc.title}
            saving={saving}
            scope={{ value: scope, onChange: setScope, spaceId, saving }}
            coverage={{
                value: draft,
                onChange: setDraft,
                // Removing a chip to probe the preview must stay one-click reversible here too, not
                // just in the candidate publish form — retyping a range from memory is error-prone.
                suggestions: coverageSuggestions(candidate?.verdict),
            }}
            notice={<ReachChangeNotice doc={doc} scope={scope} draft={draft} />}
            onSubmit={(tags, title) => {
                const error = documentScopeError(scope);
                if (error) {
                    toast.error(error);
                    return Promise.resolve(false);
                }
                return onSave(doc, { tags, title, scope, coverage: coverageDraftActive(draft) ? draft : null });
            }}
        />
    );
};

/**
 * What saving does to the models already linking this document, worked out with the rule the sync
 * applies (`reachChange`). The count is taken from the document's LINKS rather than from re-deriving its
 * matches: links are what the revoke sweep actually walks. Never a reassuring "nothing" while they load.
 */
const ReachChangeNotice: FC<{ doc: ModelDocument; scope: DocumentScope; draft: CoverageDraft }> = ({
    doc,
    scope,
    draft,
}) => {
    const [linking, loadable] = useLoadableAtomValue(modelsLinkingDocumentState(doc.id));
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    if (loadable.state === 'hasError') {
        return <Notice>The models linking this document could not be loaded — check them before saving.</Notice>;
    }
    if (!linking) return <div className="field-hint">Checking which models link this document…</div>;
    // Only the predicates decide reach; the stamp is the save's to set.
    const after = { ...scope, coverage: coverageDraftActive(draft) ? { ...draft, confirmedAt: '' } : undefined };
    const change = reachChange(doc.id, doc, after, linking, families);
    const removesRule = !!doc.coverage && !after.coverage;
    if (change.revoked.length === 0 && change.flagged.length === 0 && !removesRule) return null;
    return (
        <Notice>
            {removesRule && (
                <div className="font-medium">
                    Saving removes this document’s coverage rule; no future model gets it.
                </div>
            )}
            {change.revoked.length > 0 && <div>Unlinks {describeModels(change.revoked)} linked by rule.</div>}
            {change.flagged.length > 0 && (
                <div>
                    Flags {describeModels(change.flagged)} linked directly: they keep the document, marked as not
                    matching it.
                </div>
            )}
        </Notice>
    );
};

const Notice: FC<{ children: ReactNode }> = ({ children }) => (
    <div
        role="alert"
        className="space-y-1 rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2 text-xs leading-relaxed text-amber-800"
    >
        {children}
    </div>
);

function describeModels(models: Model[]): string {
    return `${models.length} model${models.length === 1 ? '' : 's'} (${listModelNumbers(models)})`;
}
